import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Col,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
  Accordion,
  Button,
} from "react-bootstrap";
import { loader } from "../../../loader";
import { postData, putData } from "../../../axios/apiHelper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import CommonConfirmModel from "../../../Model/CommonConfirmModel";
import axios from "axios";

export default function VwdUserManagement() {
  const path_image = process.env.REACT_APP_ASSETS_PATH_INFORMED_DESIGN;
  const userstatus = {
    "Access Requested": "access",
    Active: "active",
    Rejected: "rejected",
    Blocked: "blocked",
    Deleted: "deleted",
    Invited: "invite",
  };
  const buttonRef = useRef(null);
  const filterRef = useRef(null);
  const [userListData, setUserListData] = useState([]);
  const [allUserListData, setAllUserListData] = useState([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");
  const initialFilters = useMemo(
    () => ({
      type: [],
      region: [],
      country: [],
      status: [],
      company: [],
      role: "",
    }),
    [],
  );
  const [appliedFilter, setAppliedFilter] = useState(initialFilters);
  const [draftFilter, setDraftFilter] = useState(initialFilters);
  const [showFilter, setShowFilter] = useState(false);
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [resetDataId, setResetDataId] = useState("");
  const [commonConfirmModelFun, setCommonConfirmModelFun] = useState(() => {});
  const [popupMessage, setPopupMessage] = useState({
    message1: "",
    message2: "",
    footerButton: "Yes",
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        loader("show");
        const response = await postData("/vwd/get-user-list", {});
        if (response?.status === 200) {
          setUserListData(response?.data?.data || []);
          setAllUserListData(response?.data?.data || []);
        }
      } catch (err) {
        console.log("Error while fetching user data : ", err);
      } finally {
        loader("hide");
      }
    };
    fetchUserData();
  }, []);

  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);

    const formattedDate = date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, ". ");
    return formattedDate;
  }, []);

  const handleSort = useCallback(
    (key) => {
      if (key === sortKey)
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      else {
        setSortKey(key);
        setSortOrder("desc");
      }
    },
    [sortKey],
  );

  const filterOptions = useMemo(() => {
    const getUniqueSorted = (key) => {
      return [
        ...new Set(
          allUserListData
            .map((reader) => reader?.[key]?.trim?.() || "")
            .filter((value) => Boolean(value)),
        ),
      ].sort((a, b) => a.localeCompare(b));
    };

    return {
      type: getUniqueSorted("type"),
      region: getUniqueSorted("region"),
      country: getUniqueSorted("country"),
      status: getUniqueSorted("userStatus"),
      role: getUniqueSorted("role"),
    };
  }, [allUserListData]);

  const hasAppliedFilter = useMemo(() => {
    return (
      appliedFilter.type.length > 0 ||
      appliedFilter.region.length > 0 ||
      appliedFilter.country.length > 0 ||
      appliedFilter.status.length > 0 ||
      appliedFilter.company.length > 0 ||
      appliedFilter.role.length > 0
    );
  }, [appliedFilter]);

  const searchData = useCallback(() => {
    setUserListData((prev) => {
      let data = [...allUserListData];
      const searchText = search.toLowerCase().trim();

      data = data.filter((reader) => {
        const readerName = reader?.name?.toLowerCase().trim() || "";
        const readerRole = reader?.role?.toLowerCase().trim() || "";

        if (searchText && !readerName.includes(searchText)) return false;
        if (
          appliedFilter.type.length > 0 &&
          !appliedFilter.type.includes(reader?.type)
        )
          return false;
        if (
          appliedFilter.region.length > 0 &&
          !appliedFilter.region.includes(reader?.region)
        )
          return false;
        if (
          appliedFilter.country.length > 0 &&
          !appliedFilter.country.includes(reader?.country)
        )
          return false;
        if (
          appliedFilter.status.length > 0 &&
          !appliedFilter.status.includes(reader?.userStatus)
        )
          return false;
        if (
          appliedFilter.company.length > 0 &&
          !appliedFilter.company.includes(reader?.company)
        )
          return false;
        if (
          appliedFilter.role.length > 0 &&
          !appliedFilter.role.includes(reader?.role)
        )
          return false;

        return true;
      });

      return data;
    });
  }, [search, appliedFilter, allUserListData]);

  useEffect(() => {
    if (!search.trim().length) searchData();
  }, [search, appliedFilter, allUserListData]);

  const sortedData = useMemo(() => {
    return [...userListData].sort((a, b) => {
      const isDefaultSort = sortKey === "created" && sortOrder === "desc";
      if (isDefaultSort) {
        const defaultStatusOrder = {
          "Access Requested": 0,
          Invited: 1,
          Active: 2,
          Blocked: 3,
        };
        const statusRankA = defaultStatusOrder[a?.userStatus] ?? 4;
        const statusRankB = defaultStatusOrder[b?.userStatus] ?? 4;

        if (statusRankA !== statusRankB) return statusRankA - statusRankB;
      }

      const valueA = a[sortKey];
      const valueB = b[sortKey];

      // Handle different data types (numbers, strings)
      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortOrder === "asc" ? valueA - valueB : valueB - valueA;
      } else {
        return sortOrder === "asc"
          ? valueA?.localeCompare(valueB) // Handle string sorting with locale awareness
          : valueB?.localeCompare(valueA);
      }
    });
  }, [userListData, sortKey, sortOrder]);

  const handleSendInviteEmail = useCallback(
    async (id) => {
      const userData = userListData.find((user) => user.id === id);
      if (!userData?.email) {
        toast.error("Unable to send re-invite email.");
        return;
      }

      try {
        loader("show");
        const invitationType =
          userData?.type === "Internal" ? "internal" : "external";

        axios.defaults.baseURL = process.env.REACT_APP_API_KEY;
        const templateResponse = await axios.post(
          "emailapi/get_template_list",
          {
            user_id: localStorage.getItem("user_id"),
            language: 10,
            ibu: "",
            vwd_invitation: 1,
            invitation_type: invitationType,
            content_included: "0",
          },
        );

        const templates = templateResponse?.data?.response?.data || [];
        const selectedTemplate = templates?.[0];

        if (!selectedTemplate?.source_code) {
          toast.error("Unable to find invitation template for re-invite.");
          return;
        }

        const body = {
          user_id: localStorage.getItem("user_id"),
          route_location: "vwd/reinvite-email",
          pdf_id: "16",
          subject: "Your VWD Journey invitation is waiting",
          description: "VWD re-invitation email",
          creator: "vwd",
          campaign_name: "VWD Reinvite Campaign",
          tags: [],
          template_source_code: selectedTemplate.source_code,
          campaign_id: "",
          campaign_data: {
            user_list: [
              {
                name: userData?.name || "",
                email: userData?.email || "",
                type: userData?.type || "External",
              },
            ],
            template_id: selectedTemplate.id || 0,
            list_selection: 2,
            sublink_id: 0,
            survey_id: 0,
          },
        };

        const response = await axios.post("emailapi/send_email_new", body);
        if (response?.data?.status_code === 200) {
          toast.success("Re-invite email sent successfully.");
        } else {
          toast.error(response?.data?.message || "Failed to send re-invite.");
        }
      } catch (err) {
        console.log("Failed to send re-invite email : ", err);
        toast.error("Failed to send re-invite.");
      } finally {
        loader("hide");
      }
    },
    [userListData],
  );

  const changeUserStatus = useCallback(async (id, status) => {
    try {
      loader("show");
      const response = await putData("/vwd/change-user-status", { id, status });
      if (response.status === 200) {
        if (status === "Deleted") {
          setUserListData((prev) => prev.filter((user) => user.id !== id));
          setAllUserListData((prev) => prev.filter((user) => user.id !== id));
        } else {
          setUserListData((prev) =>
            prev.map((user) => {
              return user.id === id ? { ...user, userStatus: status } : user;
            }),
          );
          setAllUserListData((prev) =>
            prev.map((user) => {
              return user.id === id ? { ...user, userStatus: status } : user;
            }),
          );
        }
      } else {
        toast("Oops!! Failed to update user status");
      }
    } catch (err) {
      console.log("Failed to update user status : ", err);
      toast("Oops!! Failed to update user status");
    } finally {
      loader("hide");
    }
  }, []);

  const hideConfirmationModal = useCallback(() => {
    setConfirmationPopup(false);
  }, []);

  const confirmRejectUser = useCallback(
    (id) => {
      setConfirmationPopup(false);
      changeUserStatus(id, "Rejected");
    },
    [changeUserStatus],
  );

  const showRejectConfirmation = useCallback(
    (id) => {
      setResetDataId(id);
      setCommonConfirmModelFun(() => confirmRejectUser);
      setPopupMessage({
        message1: "You are about to reject this user.",
        message2: "Are you sure you want to do this?",
        footerButton: "Yes, Reject",
      });
      setConfirmationPopup(true);
    },
    [confirmRejectUser],
  );

  const confirmReinviteUser = useCallback(
    async (id) => {
      setConfirmationPopup(false);
      await handleSendInviteEmail(id);
    },
    [handleSendInviteEmail],
  );

  const showReinviteConfirmation = useCallback(
    (id) => {
      setResetDataId(id);
      setCommonConfirmModelFun(() => confirmReinviteUser);
      setPopupMessage({
        message1: "You are about to re-invite this user.",
        message2: "Do you want to send the invitation email again?",
        footerButton: "Yes, Reinvite",
      });
      setConfirmationPopup(true);
    },
    [confirmReinviteUser],
  );

  const confirmDeleteUser = useCallback(
    (id) => {
      setConfirmationPopup(false);
      changeUserStatus(id, "Deleted");
    },
    [changeUserStatus],
  );

  const showDeleteConfirmation = useCallback(
    (id) => {
      setResetDataId(id);
      setCommonConfirmModelFun(() => confirmDeleteUser);
      setPopupMessage({
        message1: "Are you sure you want to delete this user?",
        message2:
          "This user will no longer be able to access the platform, and all their records will be permanently deleted.",
        message3: "This action cannot be undone.",
        footerButton: "Delete User",
      });
      setConfirmationPopup(true);
    },
    [confirmDeleteUser],
  );

  const confirmBlockUser = useCallback(
    (id) => {
      setConfirmationPopup(false);
      changeUserStatus(id, "Blocked");
    },
    [changeUserStatus],
  );

  const showBlockConfirmation = useCallback(
    (id) => {
      setResetDataId(id);
      setCommonConfirmModelFun(() => confirmBlockUser);
      setPopupMessage({
        message1: "Are you sure you want to block this user?",
        message2:
          "If you block this user, they will no longer be able to access the platform. Their records will be kept, and you can unblock them later if needed.",
        footerButton: "Block User",
      });
      setConfirmationPopup(true);
    },
    [confirmBlockUser],
  );

  const downloadUsersExcel = (data) => {
    const formattedData = data.map((item) => ({
      Name: item.name,
      Email: item.email,
      Type: item.type,
      Role: item.role,
      Region: item.region,
      Country: item.country,
      Status: item.status === "pending" ? "Not Approved" : item.status,
      "Join Date": item.created == "N/A" ? "-" : formatDate(item.created),
      "Last Activity": item.lastActivity ? formatDate(item.lastActivity) : "-",
    }));

    // Convert JSON → worksheet
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Details");

    // Download file
    XLSX.writeFile(workbook, "User Profile.xlsx");
  };

  const clearFilter = useCallback(() => {
    setDraftFilter(initialFilters);
    setAppliedFilter(initialFilters);
    setShowFilter(false);
  }, [initialFilters]);

  const applyFilter = useCallback(() => {
    setAppliedFilter(draftFilter);
    setShowFilter(false);
  }, [draftFilter]);

  const handleFilterSelection = useCallback((key, value) => {
    setDraftFilter((prev) => {
      const selectedValues = prev[key] || [];
      const isSelected = selectedValues.includes(value);
      return {
        ...prev,
        [key]: isSelected
          ? selectedValues.filter((item) => item !== value)
          : [...selectedValues, value],
      };
    });
  }, []);

  const LinkWithTooltip = ({ id, children, href, tooltip }) => {
    return (
      <OverlayTrigger
        overlay={<Tooltip id={id}>{tooltip}</Tooltip>}
        placement="top"
        delayShow={300}
        delayHide={150}
      >
        <a href={href}>{children}</a>
      </OverlayTrigger>
    );
  };

  const tableRows = [
    { name: "name", title: "Name" },
    { name: "email", title: "Email" },
    { name: "type", title: "Type" },
    { name: "role", title: "Role" },
    { name: "region", title: "Region" },
    { name: "country", title: "Country" },
    { name: "userStatus", title: "Status" },
    { name: "created", title: "Join Date" },
    { name: "lastActivity", title: "Last Activity" },
  ];
  const handleMainClick = () => setShowFilter(false);

  return (
    <>
      <Col
        className="right-sidebar custom-change vwd-user-management"
        onClick={handleMainClick}
      >
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <div className="custom-container">
          <Row>
            <div className="top-sticky">
              <div className="top-header reader_list">
                <div className="page-title">
                  <h4>User Management</h4>
                </div>
                <div class="top-right-action flex-wrap">
                  <div class="search-bar">
                    <form
                      class="d-flex"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (search.trim().length < 3) {
                          toast(
                            "Please enter at least three characters to search",
                          );
                          return;
                        }
                        searchData();
                      }}
                    >
                      <input
                        class="form-control me-2"
                        type="search"
                        placeholder="Search by user name"
                        aria-label="Search"
                        id="email_search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <button class="btn btn-outline" type="submit">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15.8045 14.862L11.2545 10.312C12.1359 9.22334 12.6665 7.84 12.6665 6.33334C12.6665 2.84134 9.82522 0 6.33325 0C2.84128 0 0 2.84131 0 6.33331C0 9.82531 2.84132 12.6667 6.33328 12.6667C7.83992 12.6667 9.22325 12.136 10.3119 11.2547L14.8619 15.8047C14.9919 15.9347 15.1625 16 15.3332 16C15.5039 16 15.6745 15.9347 15.8045 15.8047C16.0652 15.544 16.0652 15.1227 15.8045 14.862ZM6.33328 11.3333C3.57597 11.3333 1.33333 9.09066 1.33333 6.33331C1.33333 3.57597 3.57597 1.33331 6.33328 1.33331C9.0906 1.33331 11.3332 3.57597 11.3332 6.33331C11.3332 9.09066 9.09057 11.3333 6.33328 11.3333Z"
                            fill="#97B6CF"
                          ></path>
                        </svg>
                      </button>
                    </form>
                  </div>
                  <div
                    className="filter-by nav-item dropdown"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      ref={buttonRef}
                      className={
                        hasAppliedFilter
                          ? "btn btn-secondary dropdown filter_applied"
                          : "btn btn-secondary dropdown"
                      }
                      type="button"
                      id="dropdownMenuButton2"
                      onClick={() => setShowFilter((prev) => !prev)}
                    >
                      Filter By
                      {showFilter ? (
                        <svg
                          className="close-arrow"
                          width="13"
                          height="12"
                          viewBox="0 0 13 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            width="2.09896"
                            height="15.1911"
                            rx="1.04948"
                            transform="matrix(0.720074 0.693897 -0.720074 0.693897 11.0977 0)"
                            fill="#0066BE"
                          />
                          <rect
                            width="2.09896"
                            height="15.1911"
                            rx="1.04948"
                            transform="matrix(0.720074 -0.693897 0.720074 0.693897 0 1.45898)"
                            fill="#0066BE"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="filter-arrow"
                          width="16"
                          height="14"
                          viewBox="0 0 16 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.615385 2.46154H3.07692C3.07692 3.14031 3.62892 3.69231 4.30769 3.69231H5.53846C6.21723 3.69231 6.76923 3.14031 6.76923 2.46154H15.3846C15.7243 2.46154 16 2.18646 16 1.84615C16 1.50585 15.7243 1.23077 15.3846 1.23077H6.76923C6.76923 0.552 6.21723 0 5.53846 0H4.30769C3.62892 0 3.07692 0.552 3.07692 1.23077H0.615385C0.275692 1.23077 0 1.50585 0 1.84615C0 2.18646 0.275692 2.46154 0.615385 2.46154Z"
                            fill="#97B6CF"
                          />
                          <path
                            d="M15.3846 6.15362H11.6923C11.6923 5.47485 11.1403 4.92285 10.4615 4.92285H9.23077C8.552 4.92285 8 5.47485 8 6.15362H0.615385C0.275692 6.15362 0 6.4287 0 6.76901C0 7.10931 0.275692 7.38439 0.615385 7.38439H8C8 8.06316 8.552 8.61516 9.23077 8.61516H10.4615C11.1403 8.61516 11.6923 8.06316 11.6923 7.38439H15.3846C15.7243 7.38439 16 7.10931 16 6.76901C16 6.4287 15.7243 6.15362 15.3846 6.15362Z"
                            fill="#97B6CF"
                          />
                          <path
                            d="M15.3846 11.077H6.76923C6.76923 10.3982 6.21723 9.84619 5.53846 9.84619H4.30769C3.62892 9.84619 3.07692 10.3982 3.07692 11.077H0.615385C0.275692 11.077 0 11.352 0 11.6923C0 12.0327 0.275692 12.3077 0.615385 12.3077H3.07692C3.07692 12.9865 3.62892 13.5385 4.30769 13.5385H5.53846C6.21723 13.5385 6.76923 12.9865 6.76923 12.3077H15.3846C15.7243 12.3077 16 12.0327 16 11.6923C16 11.352 15.7243 11.077 15.3846 11.077Z"
                            fill="#97B6CF"
                          />
                        </svg>
                      )}
                    </button>
                    {showFilter && (
                      <div
                        ref={filterRef}
                        className="dropdown-menu filter-options crm-filter"
                        aria-labelledby="dropdownMenuButton2"
                      >
                        <h4>Filter By</h4>
                        <div className="filter-option">
                          <Accordion defaultActiveKey="0" flush>
                            {[
                              { key: "type", label: "Type" },
                              { key: "region", label: "Region" },
                              { key: "country", label: "Country" },
                              { key: "status", label: "Status" },
                              // { key: "company", label: "Company" },
                              { key: "role", label: "Role" },
                            ].map((item, index) => (
                              <Accordion.Item
                                className="card"
                                eventKey={`${index}`}
                                key={item.key}
                              >
                                <Accordion.Header
                                  className={`card-header ${
                                    draftFilter[item.key]?.length
                                      ? "selected"
                                      : ""
                                  }`}
                                >
                                  {item.label}
                                </Accordion.Header>
                                <Accordion.Body className="card-body">
                                  <ul>
                                    {filterOptions[item.key]?.map((value) => (
                                      <li key={value}>
                                        <label className="select-multiple-option">
                                          <input
                                            type="checkbox"
                                            value={value}
                                            checked={draftFilter[
                                              item.key
                                            ]?.includes(value)}
                                            onChange={() =>
                                              handleFilterSelection(
                                                item.key,
                                                value,
                                              )
                                            }
                                          />
                                          {value}
                                          <span className="checkmark"></span>
                                        </label>
                                      </li>
                                    ))}
                                  </ul>
                                </Accordion.Body>
                              </Accordion.Item>
                            ))}
                          </Accordion>
                        </div>

                        <div className="filter-footer">
                          <Button
                            className="btn btn-primary btn-bordered"
                            onClick={clearFilter}
                          >
                            Clear
                          </Button>
                          <Button
                            className="btn btn-primary btn-filled"
                            onClick={applyFilter}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div class="clear-search">
                    <button
                      title="Download stats"
                      class="btn btn-outline-primary  "
                      onClick={() => downloadUsersExcel(userListData)}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18.3335 13.125C18.1125 13.125 17.9005 13.2128 17.7442 13.3691C17.588 13.5254 17.5002 13.7373 17.5002 13.9583V15.1775C17.4995 15.7933 17.2546 16.3836 16.8192 16.819C16.3838 17.2544 15.7934 17.4993 15.1777 17.5H4.82266C4.2069 17.4993 3.61655 17.2544 3.18114 16.819C2.74573 16.3836 2.50082 15.7933 2.50016 15.1775V13.9583C2.50016 13.7373 2.41237 13.5254 2.25609 13.3691C2.0998 13.2128 1.88784 13.125 1.66683 13.125C1.44582 13.125 1.23385 13.2128 1.07757 13.3691C0.921293 13.5254 0.833496 13.7373 0.833496 13.9583V15.1775C0.834599 16.2351 1.25524 17.2492 2.00311 17.997C2.75099 18.7449 3.76501 19.1656 4.82266 19.1667H15.1777C16.2353 19.1656 17.2493 18.7449 17.9972 17.997C18.7451 17.2492 19.1657 16.2351 19.1668 15.1775V13.9583C19.1668 13.7373 19.079 13.5254 18.9228 13.3691C18.7665 13.2128 18.5545 13.125 18.3335 13.125Z"
                          fill="#0066BE"
                        ></path>
                        <path
                          d="M14.7456 9.20249C14.5893 9.04626 14.3774 8.9585 14.1564 8.9585C13.9355 8.9585 13.7235 9.04626 13.5673 9.20249L10.8231 11.9467L10.8333 1.77108C10.8333 1.55006 10.7455 1.3381 10.5893 1.18182C10.433 1.02554 10.221 0.937744 10 0.937744C9.77899 0.937744 9.56702 1.02554 9.41074 1.18182C9.25446 1.3381 9.16667 1.55006 9.16667 1.77108L9.15643 11.9467L6.41226 9.20249C6.25509 9.05069 6.04459 8.96669 5.82609 8.96859C5.60759 8.97049 5.39858 9.05813 5.24408 9.21264C5.08957 9.36715 5.00193 9.57615 5.00003 9.79465C4.99813 10.0131 5.08213 10.2236 5.23393 10.3808L9.40059 14.5475C9.478 14.6251 9.56996 14.6867 9.6712 14.7287C9.77245 14.7707 9.88098 14.7923 9.99059 14.7923C10.1002 14.7923 10.2087 14.7707 10.31 14.7287C10.4112 14.6867 10.5032 14.6251 10.5806 14.5475L14.7473 10.3808C14.9033 10.2243 14.9907 10.0123 14.9904 9.79131C14.9901 9.57034 14.902 9.35854 14.7456 9.20249Z"
                          fill="#0066BE"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="sunshine-table mt-4">
              <div class="table-title">
                <h4>
                  Total Users <span>| {userListData?.length || 0}</span>
                </h4>
              </div>
              {userListData.length > 0 ? (
                <Table className="fold-table">
                  <thead className="sticky-header">
                    <tr>
                      {tableRows?.map((item) => (
                        <TableHeadColumn
                          name={item.name}
                          title={item.title}
                          handleSort={handleSort}
                          sortKey={sortKey}
                          sortOrder={sortOrder}
                        />
                      ))}
                      <th className="sort_option_blank"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {userListData.length > 0 &&
                      sortedData.map((data) => {
                        return (
                          <React.Fragment key={data.id}>
                            <tr>
                              <td className="blue name-cell">
                                {data?.name}{" "}
                                {data?.ibt ? (
                                  <div className="badge">
                                    <img
                                      src={path_image + "ibt-badge.png"}
                                      alt="badge"
                                      className="corner-badge-img"
                                    />
                                  </div>
                                ) : null}
                              </td>
                              <td>{data?.email || "-"}</td>
                              <td>{data?.type || "-"}</td>
                              <td>{data?.role || "-"}</td>
                              <td>{data?.region || "-"}</td>
                              <td>{data?.country || "-"}</td>
                              <td
                                className={userstatus[data?.userStatus] || ""}
                              >
                                {data?.userStatus || "-"}
                              </td>
                              <td>
                                {data?.created == "N/A"
                                  ? "-"
                                  : formatDate(data?.created)}
                              </td>
                              <td className="blue">
                                {data?.lastActivity
                                  ? formatDate(data?.lastActivity)
                                  : "-"}
                              </td>
                              <td className="divide-line">
                                <div class="action-buttons">
                                  {data?.userStatus === "Access Requested" ? (
                                    <>
                                      <LinkWithTooltip
                                        id="decline-tooltip"
                                        tooltip="Decline"
                                      >
                                        <a>
                                          <button
                                            type="button"
                                            class="decline btn btn-primary"
                                            onClick={() =>
                                              showRejectConfirmation(data.id)
                                            }
                                          >
                                            <img
                                              src="../../../componentAssets/images/decline.svg"
                                              alt="decline"
                                              width="16"
                                            />
                                          </button>
                                        </a>
                                      </LinkWithTooltip>
                                      <LinkWithTooltip
                                        id="accept-tooltip"
                                        tooltip="Accept"
                                      >
                                        <a>
                                          <button
                                            type="button"
                                            class="accept btn btn-primary"
                                            onClick={() =>
                                              changeUserStatus(
                                                data.id,
                                                "Active",
                                              )
                                            }
                                          >
                                            <img
                                              src="../../../componentAssets/images/accept.svg"
                                              alt="accept"
                                              width="16"
                                            />
                                          </button>
                                        </a>{" "}
                                      </LinkWithTooltip>
                                    </>
                                  ) : (
                                    <>
                                      <LinkWithTooltip
                                        id="delete-tooltip"
                                        tooltip="Delete user"
                                      >
                                        <a>
                                          <button
                                            type="button"
                                            class="btn-delete btn btn-primary"
                                            onClick={() =>
                                              showDeleteConfirmation(data.id)
                                            }
                                          >
                                            <img
                                              src="https://informed.pro/componentAssets/images/delete-icon.svg"
                                              alt="delete"
                                              width="16"
                                            />
                                          </button>
                                        </a>
                                      </LinkWithTooltip>
                                      {data?.userStatus !== "Invited" ? (
                                        data?.userStatus === "Blocked" ? (
                                          <LinkWithTooltip
                                            id="block-tooltip"
                                            tooltip="Unblock user"
                                          >
                                            <a>
                                              <button
                                                type="button"
                                                class="user-blocked btn btn-primary"
                                                onClick={() =>
                                                  changeUserStatus(
                                                    data.id,
                                                    "Active",
                                                  )
                                                }
                                              >
                                                <img
                                                  src="https://informed.pro/componentAssets/images/user-blocked.svg"
                                                  alt="delete"
                                                  width="16"
                                                />
                                              </button>
                                            </a>
                                          </LinkWithTooltip>
                                        ) : (
                                          <LinkWithTooltip
                                            id="block-tooltip"
                                            tooltip="Block user"
                                          >
                                            <a>
                                              <button
                                                type="button"
                                                class="user btn btn-primary"
                                                onClick={() =>
                                                  showBlockConfirmation(data.id)
                                                }
                                              >
                                                <img
                                                  src="https://informed.pro/componentAssets/images/user-blocked.svg"
                                                  alt="delete"
                                                  width="16"
                                                />
                                              </button>
                                            </a>
                                          </LinkWithTooltip>
                                        )
                                      ) : null}

                                      {data?.userStatus === "Invited" && (
                                        <LinkWithTooltip
                                          id="resend-tooltip"
                                          tooltip="Resend invite"
                                        >
                                          <a>
                                            <button
                                              type="button"
                                              class="email btn btn-primary"
                                              onClick={() =>
                                                showReinviteConfirmation(
                                                  data.id,
                                                )
                                              }
                                            >
                                              <img
                                                src="https://informed.pro/componentAssets/images/email-icon1.svg"
                                                alt="add"
                                                width="16"
                                              />
                                            </button>
                                          </a>
                                        </LinkWithTooltip>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                  </tbody>
                </Table>
              ) : (
                <div class="email_box_block no_found rd">
                  <p>No Data Found</p>
                </div>
              )}
            </div>
          </Row>
        </div>
      </Col>
      <CommonConfirmModel
        show={confirmationPopup}
        onClose={hideConfirmationModal}
        onCloseCross={hideConfirmationModal}
        fun={commonConfirmModelFun}
        popupMessage={popupMessage}
        path_image={path_image}
        resetDataId={resetDataId}
      />
    </>
  );
}

const TableHeadColumn = ({ name, title, sortKey, sortOrder, handleSort }) => {
  const sortButtonClass =
    sortKey === name ? (sortOrder === "asc" ? "svg_asc" : "svg_active") : "";

  return (
    <th className="sort_option">
      <span onClick={() => handleSort(name)}>
        {title}
        <button className={`event_sort_btn ${sortButtonClass}`}>
          {" "}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
          >
            {" "}
            <g clipPath="url(#clip0_3722_6611)">
              {" "}
              <path
                d="M7.00015 5.19137L4.3311 7.84461C4.28138 7.89413 4.22222 7.93328 4.15708 7.95976C4.02649 8.01341 3.87983 8.01341 3.74925 7.95976C3.6841 7.93328 3.62494 7.89413 3.57522 7.84461L0.90617 5.19137C0.806076 5.09173 0.7499 4.95664 0.75 4.81582C0.7501 4.67501 0.806468 4.54 0.906704 4.4405C1.00694 4.341 1.14283 4.28516 1.28449 4.28526C1.42614 4.28536 1.56195 4.34139 1.66205 4.44103L3.41988 6.18845L3.41357 0.530648C3.41357 0.389912 3.46981 0.254939 3.56992 0.155423C3.67003 0.0559068 3.8058 4.76837e-07 3.94738 4.76837e-07C4.08895 4.76837e-07 4.22473 0.0559068 4.32484 0.155423C4.42495 0.254939 4.48119 0.389912 4.48119 0.530648L4.48751 6.18845L6.24534 4.44103C6.34602 4.34437 6.48086 4.29088 6.62083 4.29209C6.76079 4.2933 6.89468 4.34911 6.99365 4.44749C7.09262 4.54588 7.14876 4.67897 7.14998 4.81811C7.1512 4.95724 7.09739 5.09129 7.00015 5.19137Z"
                fill="#97B6CF"
              />{" "}
            </g>{" "}
            <defs>
              {" "}
              <clipPath id="clip0_3722_6611">
                {" "}
                <rect width="8" height="8" fill="white" />{" "}
              </clipPath>{" "}
            </defs>{" "}
          </svg>{" "}
        </button>
      </span>
    </th>
  );
};
